import React, { useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Container } from '../../components/Container';
import { WindowContext } from '../../providers';
import { isInViewport } from '../../services/isInViewport';
import { parseHTML } from '../../services/parseHTML';
import { scrollToRef } from '../../services/scrollToRef';
import { Storyblok, StoryblokSection } from '../../storyblok';
import { slugify } from '../../utils';

import {
    Content,
    Entries,
    Entry,
    EntryIcon,
    Heading,
    Section,
    TemporaryInformation,
} from './style';

export const ScoreSources = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.ScoreSources>) => {
    const navigate = useNavigate();
    const { isMobile } = useContext(WindowContext);
    const tempInfoRef = useRef(null);
    const [displayTempInfo, setDisplayTempInfo] = useState(false);

    const handleSourceClick = useCallback(
        (
            event: Storyblok.Component.Source['event'],
            redirectUri: Storyblok.Component.Source['redirectUri'],
        ) => {
            if (event === 'temporaryMessage') {
                setDisplayTempInfo(true);
                /**
                 * If the message is not fully visible in
                 * the viewport the smooth scroll to it
                 */
                if (isMobile()) {
                    setTimeout(() => {
                        if (!isInViewport(tempInfoRef.current)) {
                            scrollToRef(tempInfoRef, -100);
                        }
                    }, 200);
                }
            }

            if (event === 'redirect' && redirectUri) {
                navigate(redirectUri);
            }
        },
        [navigate, isMobile],
    );

    if (!sectionData) return null;

    const { headline, content, sources, temp_info, component } = sectionData;
    return (
        <Section data-block={component}>
            <Container sizeGroup="size-1">
                {headline && (
                    <Heading>{parseHTML(headline, 'markdown')}</Heading>
                )}
                <Entries>
                    {sources &&
                        sources.length &&
                        sources.map((source) => {
                            const { label, icon, color, event, redirectUri } =
                                source;

                            return (
                                <Entry
                                    style={{ color: color || '#000000' }}
                                    onClick={() =>
                                        handleSourceClick(event, redirectUri)
                                    }
                                    onKeyPress={() =>
                                        handleSourceClick(event, redirectUri)
                                    }
                                    role="button"
                                    tabIndex={0}
                                    data-event={`source button (${slugify(
                                        label,
                                    )})`}
                                    key={slugify(label)}
                                >
                                    {icon && (
                                        <EntryIcon image={icon} objectFit />
                                    )}

                                    {label}
                                </Entry>
                            );
                        })}
                </Entries>
                {displayTempInfo && (
                    <TemporaryInformation ref={tempInfoRef}>
                        {temp_info && parseHTML(temp_info, 'markdown')}
                    </TemporaryInformation>
                )}
                <Content>{content && parseHTML(content, 'markdown')}</Content>
            </Container>
        </Section>
    );
};
